/**
 * Opaque handle describing a per-instance bridge resource: how to create its
 * state and how to tear it down when the BridgeContextObject is destroyed.
 *
 * Declare one resource per feature module (keep it private to the module unless
 * sharing across methods is intentional).
 *
 * Future lifecycle hooks (e.g. `onUpdate`, `onContextTokenRefresh`) can be
 * added here without changing existing callsites.
 */
export type BridgeResource<T> = {
	/** Called once on first `claimBridgeResource` access to initialise state. */
	create: () => T;
	/**
	 * Called by `BridgeContextObject.destroy()` on component unmount.
	 * Responsible for releasing all resources held by `state`.
	 */
	destroy: (state: T) => void;
	/** Unique identity for this resource slot. */
	id: symbol;
};

export const createBridgeResource = <T>(
	debugName: string,
	create: () => T,
	destroy: (state: T) => void,
): BridgeResource<T> => ({
	id: Symbol(debugName),
	create,
	destroy,
});

export type ClaimBridgeResource = <T>(resource: BridgeResource<T>) => T;
